import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { enumIsfShipmentCode } from '@app/data-layer/enums/isf';
import { ShipmentModel } from '@app/data-layer/models/common';
import { IsfModel } from '@app/data-layer/models/isf';
import { FormHelper } from '@helpers/form.helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IsfOcrQuickFixService, IsfStateService } from '@shared/isf-mutual/services';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { ReplaySubject } from 'rxjs';
import { debounceTime, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-isf-ocr-quick-fix-bl-details',
  templateUrl: './isf-ocr-quick-fix-bl-details.component.html',
  styleUrls: ['./isf-ocr-quick-fix-bl-details.component.scss'],
})
export class IsfOcrQuickFixBlDetailsComponent {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public isf: IsfModel;
  public formGroup: UntypedFormGroup;
  public blControl: UntypedFormControl;
  public billFormGroup: UntypedFormGroup;
  public errors: { [name: string]: IErrorForm } = {
    billCarrier: {
      required: 'FORM_ERROR.REQUIRED',
      wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
      minlength: 'FORM_ERROR.MIN_LENGTH',
      maxlength: 'FORM_ERROR.MAX_LENGTH',
    },
    billNumber: {
      required: 'FORM_ERROR.REQUIRED',
      wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
      minlength: 'FORM_ERROR.MIN_LENGTH',
      maxlength: 'FORM_ERROR.MAX_LENGTH',
    },
  };
  public enumIsfShipmentCode: typeof enumIsfShipmentCode = enumIsfShipmentCode;
  public shipment: ShipmentModel;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private fh: FormHelper, private isfOcrQuickFixService: IsfOcrQuickFixService, private isfStateService: IsfStateService) {}

  ngOnInit(): void {
    const isf = this.isfOcrQuickFixService.getIsf();
    this.syncIsf(isf);

    this.initializeFormGroup();

    this.fh.updateEntityValidation(true, this.formGroup);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[], fg: UntypedFormGroup): boolean {
    let isFgInvalid: boolean = false;
    Object.keys(fg.controls).forEach((key) => {
      if (fg.controls[key].invalid) {
        isFgInvalid = true;
        return;
      }
    });

    let invalidFields = blockFields.find((field) => fg.get(field.name)?.invalid || !fg.get(field.name)?.value || isFgInvalid);
    return !!invalidFields;
  }

  public onSubmit(isNeedCheck: boolean = true): void {
    if (isNeedCheck && this.formGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.formGroup);
      if (Object.keys(invalidFields).length) {
        this.fh.markAsTouched(this.formGroup);
        this.openMissingFieldsModal(invalidFields);
        return;
      }
    }

    this.updateData()
      .pipe(
        switchMap(() => this.isfStateService.getSummaryById(this.isfStateService?.currentIsf?.referenceNumber)),
        take(1),
      )
      .subscribe((isf: IsfModel) => {
        this.isfStateService.currentIsf = isf;
        this.dataSaved.emit();
      });
  }

  public onClearForm(): void {
    this.formGroup.reset();
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private initializeFormGroup() {
    this.formGroup = this.fh.createIsfShipmentDetailsFormGroup();
    this.blControl = this.formGroup.get('code') as UntypedFormControl;
    this.billFormGroup = this.formGroup.get('bill') as UntypedFormGroup;

    this.billFormGroup.valueChanges.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe();

    this.patchData();
  }

  private updateData() {
    return this.isfStateService.updateShipment(this.prepareData(), true);
  }

  private prepareData(): ShipmentModel {
    return {
      ...this.isfStateService.currentIsf.shipment,
      code: this.blControl.value,
      masterBill: this.blControl.value !== enumIsfShipmentCode.HOUSE ? this.formGroup.get('bill').value : { carrier: null, number: null },
      houseBill: this.blControl.value === enumIsfShipmentCode.HOUSE ? this.formGroup.get('bill').value : { carrier: null, number: null },
    };
  }

  private patchData() {
    const shipment: ShipmentModel = this.isfStateService.getShipment();
    this.shipment = shipment;

    this.fh.patchIsfShipmentDetailsFormGroup(this.formGroup, this.isfStateService.currentIsf?.type, this.shipment);
  }

  private syncIsf(isf: IsfModel) {
    this.isf = isf;
  }
}
